import { DateTime } from "luxon";

import { IInboxConversation, InboxChannel, InboxFolder } from "../models/messagesInbox/IInboxConversation";
import { detectRegs } from "./detectRegs";

export type InboxSort = "least_time_left" | "oldest_first";

/** Cancellations, address changes and order changes need answering first (Stuart, 27 Oct 2026). */
export const URGENT_TYPES: IInboxConversation["type"][] = ["cancellation", "address_change", "amendment"];

export function isUrgent(c: IInboxConversation) {
  return c.status === "needs_reply" && URGENT_TYPES.includes(c.type);
}

/** When we last replied (by hand or automatically), or undefined if we never have. */
export function lastSentAt(c: IInboxConversation) {
  // Live list rows have no messages until opened, so they carry it from the API row instead.
  return c.messages
    .filter((m) => m.direction === "us" || m.direction === "auto")
    .map((m) => m.sentAt)
    .concat(c.lastSentAt ? [c.lastSentAt] : [])
    .sort()
    .pop();
}

/** Has an order: the full order once the part context is in, or the order number on the row. */
const hasOrder = (c: IInboxConversation) => !!c.order || !!c.orderNumber;

/** Folder membership, per spec §2–3 (no snoozing: dropped 27 Oct 2026). */
export function inFolder(c: IInboxConversation, folder: InboxFolder) {

  switch (folder) {
    case "needs_reply":
      return c.status === "needs_reply";
    case "sent":
      // Replied to and not waiting on us again: a sent reply moves the conversation here.
      return c.status === "waiting_on_buyer" && !!lastSentAt(c);
    case "no_reply_needed":
      return c.status === "no_reply_needed";
    case "pre_sales":
      return !hasOrder(c);
    case "post_sales":
      return hasOrder(c);
    case "returns":
      return (!!c.ebayCase || c.type === "return" || !!c.returnCase);
    case "all":
    default:
      return true;
  }
}

export function matchesChannel(c: IInboxConversation, channel: InboxChannel | "all") {
  return channel === "all" || c.channel === channel;
}

/** Search across buyer, part, tag no., order no. and any reg in the conversation. */
export function matchesSearch(c: IInboxConversation, search: string) {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const compactTerm = term.replace(/\s+/g, "");

  const regs = [c.fitment?.reg, c.vehicleVrm, ...c.messages.flatMap((m) => (m.direction === "buyer" ? detectRegs(m.body) : []))];
  const haystack = [
    c.buyer.username,
    c.buyer.fullName,
    c.part?.title,
    c.part?.tagNumber,
    c.itemTitle,
    c.subject,
    c.partTagNumber,
    c.orderNumber,
    c.part?.oePartNumber,
    c.donor?.engineCode,
    c.donor?.gearboxCode,
    c.order?.orderNumber,
    c.summary,
  ];

  return (
    haystack.some((v) => v?.toLowerCase().includes(term)) ||
    regs.some((r) => r?.toLowerCase().includes(compactTerm))
  );
}

/**
 * Default sort is least time left to reply: conversations that need a reply come first, the
 * nearest (or most overdue) deadline at the top; everything else follows, newest first.
 */
export function sortConversations(list: IInboxConversation[], sort: InboxSort) {
  const byTime = (iso: string) => DateTime.fromISO(iso, { zone: "utc" }).toMillis();

  return [...list].sort((a, b) => {
    if (sort === "oldest_first") return byTime(a.lastBuyerMessageAt) - byTime(b.lastBuyerMessageAt);

    // Urgent first, then everything else that needs a reply, then the rest.
    const rank = (c: IInboxConversation) => (isUrgent(c) ? 0 : c.status === "needs_reply" ? 1 : 2);
    const aNeeds = rank(a);
    const bNeeds = rank(b);
    if (aNeeds !== bNeeds) return aNeeds - bNeeds;
    if (aNeeds < 2) return byTime(a.replyBy) - byTime(b.replyBy);
    return byTime(b.lastBuyerMessageAt) - byTime(a.lastBuyerMessageAt);
  });
}

/** Sent folder: most recently replied to first, like a mail Sent box. */
export function sortBySent(list: IInboxConversation[]) {
  return [...list].sort((a, b) => (lastSentAt(b) ?? "").localeCompare(lastSentAt(a) ?? ""));
}

/** Overdue = needs a reply and past its reply-by time. */
export function isOverdue(c: IInboxConversation, now: DateTime) {
  return c.status === "needs_reply" && DateTime.fromISO(c.replyBy, { zone: "utc" }) < now;
}
